import type { CompanyOwner, IDataForm } from "@/context/FormDataContext";

export const BR_DDD: string[] = [
  "68",
  "82",
  "96",
  "92",
  "97",
  "71",
  "73",
  "74",
  "75",
  "77",
  "85",
  "88",
  "27",
  "28",
  "61",
  "62",
  "64",
  "98",
  "99",
  "65",
  "66",
  "67",
  "31",
  "32",
  "33",
  "34",
  "35",
  "37",
  "38",
  "91",
  "93",
  "94",
  "83",
  "41",
  "42",
  "43",
  "44",
  "45",
  "46",
  "81",
  "87",
  "86",
  "89",
  "21",
  "22",
  "24",
  "84",
  "51",
  "53",
  "54",
  "55",
  "69",
  "95",
  "47",
  "48",
  "49",
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "19",
  "79",
  "63",
];

/**
 * Expects x to be a valid BR Phone number
 * @param {string | undefined } x
 * @return {boolean}
 */
export function toBeBrPhoneNumber(x: string | undefined): boolean {
  return (
    (x &&
      ((x.length >= 8 &&
        x.length <= 11 &&
        BR_DDD?.includes(x.substring(0, 2))) ||
        (x.length >= 12 && x.length <= 14 && x.substring(0, 2) === "55"))) ||
    false
  );
}

/**
 * Update phone mask
 * @param {string} phoneValue
 * @param {string} setMask
 * @return {void}
 */
export function updatePhoneMask(
  phoneValue: string,
  setMask: (mask: string) => void
): void {
  const plainValue = phoneValue.replace(/[^\d]/g, "");
  const plainValueLength = plainValue.length;
  if (plainValueLength > 10) {
    setMask("(99) 99999-99999");
  } else {
    setMask("(99) 9999-99999");
  }
}

/**
 * Validate CPF
 * @param {string | number | undefined} cpf cpf
 * @return {boolean} valid or invalid
 */
export const validCpf = (cpf: string | number | undefined): boolean => {
  const cpfAux: string = (cpf + "").replace(/[^\d]+/g, "");

  // Check for empty
  if (cpfAux == "") {
    return false;
  }

  // Check for length
  if (cpfAux.length != 11) {
    return false;
  }

  // Eliminate kwons CPFs
  if (
    cpfAux == "00000000000" ||
    cpfAux == "11111111111" ||
    cpfAux == "22222222222" ||
    cpfAux == "33333333333" ||
    cpfAux == "44444444444" ||
    cpfAux == "55555555555" ||
    cpfAux == "66666666666" ||
    cpfAux == "77777777777" ||
    cpfAux == "88888888888" ||
    cpfAux == "99999999999"
  ) {
    return false;
  }

  // Check first digit
  let add = 0;
  for (let i = 0; i < 9; i++) {
    add += parseInt(cpfAux.charAt(i)) * (10 - i);
  }
  let rev = 11 - (add % 11);
  if (rev == 10 || rev == 11) {
    rev = 0;
  }
  if (rev != parseInt(cpfAux.charAt(9))) {
    return false;
  }

  // Check second digit
  add = 0;
  for (let i = 0; i < 10; i++) {
    add += parseInt(cpfAux.charAt(i)) * (11 - i);
  }
  rev = 11 - (add % 11);
  if (rev == 10 || rev == 11) {
    rev = 0;
  }
  // eslint-disable-next-line sonarjs/prefer-single-boolean-return
  if (rev != parseInt(cpfAux.charAt(10))) {
    return false;
  }

  // If everyting pass, return valid cpf
  return true;
};

/**
 * Read file as base64
 * @param {File} file
 * @return {Promise<string>}
 */
export function fileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.readAsDataURL(file);
    fr.addEventListener("load", () => {
      const result = fr.result as string;
      // Remove base64 prefix
      resolve(result.substring(result.indexOf("base64,") + 7, result.length));
    });
    fr.addEventListener("error", reject);
  });
}

/**
 *
 * @param {string} x
 * @return {string}
 */
export function cleanNonDigits(x: string): string {
  return String(x).trim().replace(/[^\d]/g, "");
}

/**
 * Removes HTML tags from a string while preserving plain text.
 * Safe no-op for empty values; non-string values are coerced only when provided as string-like.
 * @param {string | null | undefined} value
 * @return {string}
 */
export function stripHtml(value: string | null | undefined): string {
  if (value == null) {
    return "";
  }
  return String(value).replace(/<[^>]*>/g, "").trim();
}

/**
 * Sanitizes free-text fields on a lead payload before API submit.
 * Does not alter phones, documents, numbers, flags, ids, or metadata.
 * @param {IDataForm} formData
 * @return {IDataForm}
 */
export function sanitizeLeadPayload(formData: IDataForm): IDataForm {
  const companyOwners: CompanyOwner[] = Array.isArray(formData.companyOwners)
    ? formData.companyOwners.map((owner) => ({
        ...owner,
        name: stripHtml(owner?.name),
        email: stripHtml(owner?.email),
      }))
    : formData.companyOwners;

  return {
    ...formData,
    name: stripHtml(formData.name),
    email: stripHtml(formData.email),
    companyName: stripHtml(formData.companyName),
    group:
      typeof formData.group === "string"
        ? stripHtml(formData.group)
        : formData.group,
    share:
      typeof formData.share === "string"
        ? stripHtml(formData.share)
        : formData.share,
    contractId:
      typeof formData.contractId === "string"
        ? stripHtml(formData.contractId)
        : formData.contractId,
    attachmentName:
      typeof formData.attachmentName === "string"
        ? stripHtml(formData.attachmentName)
        : formData.attachmentName,
    companyOwners,
  };
}